import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../ui/Button';

import logoPre from '../../assets/images/pre/logo.png';
import avatar from '../../assets/images/logos/avatar.png';
import messageIcon from '../../assets/images/pre/message.svg';
import boxIcon from '../../assets/images/pre/box.svg';
import groupIcon from '../../assets/images/pre/group.svg';
import frameIcon from '../../assets/images/pre/frame.svg';
import podsIcon from '../../assets/images/pre/pods.svg';
import settingIcon from '../../assets/images/pre/setting.svg';
import verifyIcon from '../../assets/images/pre/verify.svg';

interface BaoMobileMenuDropdownProps {
  user: any;
  toggleMobileMenu: () => void;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  highlightChats: boolean;
  handleLogout: () => Promise<void>;
}

const BaoMobileMenuDropdown: React.FC<BaoMobileMenuDropdownProps> = ({
  user,
  toggleMobileMenu,
  setIsMobileMenuOpen,
  highlightChats,
  handleLogout,
}) => {
  const navigate = useNavigate();

  return (
    <div className="md:hidden fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header with Logo, Translation Toggle, and Close Button */}
      <div className="flex justify-between items-center p-4 flex-shrink-0">
        {/* Logo */}
        <div className="flex items-center">
          <img
            src={logoPre}
            alt="bao'Afrik"
            className="h-6 w-auto"
          />
        </div>

        {/* Translation Toggle and Close Button */}
        <div className="flex items-center space-x-2">
          {/* Translation Toggle */}
          <div className="relative">
          </div>

          {/* Close Button */}
          <Button
            type="button"
            unstyled
            onClick={toggleMobileMenu}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none transition-all duration-200"
            aria-label="Close navigation menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
      </div>

      {/* Menu Content - Scrollable */}
      <div className="px-6 py-4 flex-1 overflow-y-auto">
        {user ? (
          // Logged in user mobile menu - Matches Desktop
          <div className="space-y-4">
            {/* Profile Section */}
            <div className="flex space-x-3 py-3 border-b border-gray-100 items-center">
              <Link
                to="/account"
                className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full hover:ring-1 hover:ring-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                onClick={() => {
                  navigate('/account');
                  setIsMobileMenuOpen(false);
                }}
                title="Profile"
                aria-label="Go to profile page"
              >
                <img
                  src={user?.profileImage || avatar}
                  alt="User Icon"
                  className="w-10 h-10 rounded-full object-cover"
                  width="48"
                  height="48"
                />
              </Link>
              <div className="flex-1">
                <p className="text-xs text-gray-500">My profile</p>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.firstName
                        ? user.firstName
                        : user?.lastName
                          ? user.lastName
                          : user?.email
                            ? user.email.split("@")[0]
                            : "User"}
                    {user?.isVerifiedSeller && (
                      <img src={verifyIcon} alt="Verified" className="w-3.5 h-3.5 inline-block shrink-0" />
                    )}
                  </h3>
                </div>
              </div>
            </div>

            {/* Create a new listing button */}
            <div className="py-3">
              <Link
                to="/create-listing"
                className="block w-full px-3 py-2 rounded-lg font-medium text-sm transition-colors duration-200 focus:outline-none"
                style={{ backgroundColor: '#E3F2FD', color: '#64B5F6' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center justify-center space-x-1.5">
                  <span>Create a new listing</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#64B5F6' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
              </Link>
            </div>

            {/* Navigation Menu Items */}
            <div className="space-y-0.5">
              {/* Chats */}
              <Link
                to="/messages"
                className={`flex items-center justify-between px-3 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg ${highlightChats ? 'bg-gray-50' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <img src={messageIcon} alt="Message" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-sm" style={{ color: '#6A6A6A' }}>Chats</div>
                  </div>
                </div>
              </Link>

              {/* My listings */}
              <Link
                to="/my-listings"
                className="flex items-center justify-between px-3 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <img src={boxIcon} alt="Box" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-sm" style={{ color: '#6A6A6A' }}>My listings</div>
                  </div>
                </div>
              </Link>

              {/* My requests */}
              <Link
                to="/my-requests"
                className="flex items-center justify-between px-3 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <img src={groupIcon} alt="Group" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-sm" style={{ color: '#6A6A6A' }}>My requests</div>
                  </div>
                </div>
              </Link>

              <Link
                to="/bookmarks"
                className="flex items-center justify-between px-3 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <img src={frameIcon} alt="" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-sm" style={{ color: '#6A6A6A' }}>Bookmarks</div>
                  </div>
                </div>
              </Link>

              {/* Help Centre */}
              <Link
                to="/help-center"
                className="flex items-center justify-between px-3 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <img src={podsIcon} alt="Pods" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-sm" style={{ color: '#6A6A6A' }}>Help Centre</div>
                  </div>
                </div>
              </Link>

              {/* Settings */}
              <Link
                to="/settings"
                className="flex items-center justify-between px-3 py-3 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <img src={settingIcon} alt="Setting" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-sm" style={{ color: '#6A6A6A' }}>Settings</div>
                    <div className="text-xs text-gray-500">Set your account preferences</div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Logout Button */}
            <div className="pt-3 border-t border-gray-100">
              <Button
                type="button"
                unstyled
                onClick={async () => {
                  await handleLogout();
                }}
                className="w-full bg-gray-100 px-3 py-2 rounded-lg transition-colors duration-200 focus:outline-none"
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#6A6A6A' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <div className="text-left">
                    <div className="font-medium text-xs" style={{ color: '#6A6A6A' }}>Log Out</div>
                    <div className="text-xs" style={{ color: '#6A6A6A' }}>Log out of BAO Afrik</div>
                  </div>
                </div>
              </Button>
            </div>
          </div>
        ) : (
          // Not logged in mobile menu - Visitor
          <div className="space-y-6">
            {/* Profile Section */}
            <div className="flex items-center space-x-3 py-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">My profile</p>
                <h3 className="text-lg font-bold text-gray-900">Me</h3>
              </div>
            </div>

            {/* Sign In and Sign Up Buttons */}
            <div className="space-y-4">
              <Link
                to="/login"
                className="block w-full text-center px-8 py-4 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors font-medium text-lg"
                style={{ backgroundColor: '#F9A822' }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#E6941F'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#F9A822'}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="block w-full text-center px-8 py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors font-medium text-lg border"
                style={{ borderColor: '#F9A822', color: '#F9A822' }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#FFF8F0';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign Up
              </Link>
            </div>

            {/* Menu Items */}
            <div className="space-y-1">
              {/* Notifications */}
              <Link
                to="/notifications"
                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-3">
                  <img src={boxIcon} alt="Box" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-gray-900">Notifications</div>
                    <div className="text-sm text-gray-500">Don't miss anything about your activities</div>
                  </div>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>

              {/* Request & Bring */}
              <Link
                to="/request-bring"
                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-3">
                  <img src={groupIcon} alt="Group" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-gray-900">Request & Bring</div>
                    <div className="text-sm text-gray-500">Lorem ipsum dolor sit amet consectetur.</div>
                  </div>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>

              {/* Bookmarks - Disabled */}
              <div
                className="flex items-center justify-between px-3 py-2 transition-colors rounded-lg"
                style={{ cursor: 'not-allowed', opacity: 0.5 }}
              >
                <div className="flex items-center space-x-3">
                  <img src={frameIcon} alt="Frame" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-gray-900">Bookmarks</div>
                  </div>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Help Centre */}
              <Link
                to="/help-center"
                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-3">
                  <img src={podsIcon} alt="Pods" className="w-5 h-5" style={{ color: '#64B5F6' }} />
                  <div>
                    <div className="font-medium text-gray-900">Help Centre</div>
                    <div className="text-sm text-gray-500">Need to talk ? We're listening</div>
                  </div>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BaoMobileMenuDropdown;
